/**
 * What points are worth. `typicalCpp` is a published valuation (cents per point) for an
 * ordinary good use; sweet spots are well-known redemptions that do much better.
 */

export interface SweetSpot {
  title: string;
  cppLow: number;
  cppHigh: number;
  note?: string;
}

export interface ProgramInfo {
  id: string;
  name: string;
  kind: 'bank' | 'airline' | 'hotel';
  typicalCpp: number;
  /** At or above this, a redemption is a good one. */
  goodCpp: number;
  sweetSpots: SweetSpot[];
  /** Main transfer partners, for bank points. */
  transfersTo?: string[];
  source: string;
}

// The Points Guy monthly valuations, October 2026, with sweet spots as of then.
// Refresh every few months: https://thepointsguy.com/guide/monthly-valuations/
export const PROGRAMS: ProgramInfo[] = [
  {
    id: 'amex_mr',
    name: 'Amex Membership Rewards',
    kind: 'bank',
    typicalCpp: 2,
    goodCpp: 2,
    source: 'The Points Guy, Oct 2026 (Frequent Miler floor 1.5)',
    sweetSpots: [
      {
        title: 'ANA business to Japan via Virgin Atlantic',
        cppLow: 4,
        cppHigh: 7,
        note: 'About 52.5k to 60k one way; book by phone',
      },
      {
        title: 'Lufthansa or Swiss business to Europe via Aeroplan',
        cppLow: 3,
        cppHigh: 6,
      },
      {
        title: 'Qatar Qsuites via Avios',
        cppLow: 3,
        cppHigh: 6,
      },
    ],
    transfersTo: [
      '1:1 Aeroplan, ANA, Avios, Delta, Flying Blue, Singapore, Virgin, Marriott',
      '1:2 Hilton',
      '5:4 Cathay, Emirates, JetBlue',
    ],
  },
  {
    id: 'chase_ur',
    name: 'Chase Ultimate Rewards',
    kind: 'bank',
    typicalCpp: 2.05,
    goodCpp: 2,
    source: 'The Points Guy, Oct 2026 (Frequent Miler floor 1.5)',
    sweetSpots: [
      {
        title: 'Hyatt category 1 to 4 nights',
        cppLow: 2,
        cppHigh: 4,
        note: '1:1 from Sapphire Reserve; 4:3 from Sapphire Preferred and Ink Preferred since Oct 1, 2026',
      },
      {
        title: 'Lufthansa business via Aeroplan',
        cppLow: 3,
        cppHigh: 6,
      },
      {
        title: 'Domestic flights via Southwest or United',
        cppLow: 1.3,
        cppHigh: 2,
      },
    ],
    transfersTo: [
      '1:1 Aeroplan, Avios, Flying Blue, JetBlue, Singapore, Southwest, United, Virgin, IHG, Marriott',
      'Hyatt 1:1 (Reserve) or 4:3 (Preferred cards)',
    ],
  },
  {
    id: 'citi_ty',
    name: 'Citi ThankYou',
    kind: 'bank',
    typicalCpp: 1.9,
    goodCpp: 1.9,
    source: 'The Points Guy, Oct 2026 (Frequent Miler floor 1.5)',
    sweetSpots: [
      {
        title: 'American partner awards: Qatar, JAL, Cathay',
        cppLow: 3,
        cppHigh: 6,
        note: 'Citi is the only bank that transfers to American',
      },
      {
        title: 'Turkish Star Alliance business',
        cppLow: 2.5,
        cppHigh: 5,
      },
      {
        title: 'JAL Mileage Bank',
        cppLow: 2.5,
        cppHigh: 5,
      },
    ],
    transfersTo: [
      '1:1 American, Cathay, Flying Blue, JetBlue, Qatar, Singapore, Turkish, Virgin, Wyndham',
      '1:1.5 Choice',
      '5:4 Emirates',
    ],
  },
  {
    id: 'capital_one',
    name: 'Capital One Miles',
    kind: 'bank',
    typicalCpp: 1.85,
    goodCpp: 1.8,
    source: 'The Points Guy, Oct 2026 (Frequent Miler floor 1.45)',
    sweetSpots: [
      {
        title: 'Turkish Star Alliance business',
        cppLow: 2.5,
        cppHigh: 5,
      },
      {
        title: 'Avios short flights and Qatar',
        cppLow: 2,
        cppHigh: 5,
      },
    ],
    transfersTo: [
      '1:1 Aeroplan, Avios, Cathay, Qatar, Singapore, Turkish, Virgin, Wyndham',
      '4:3 Emirates, EVA, JAL',
    ],
  },
  {
    id: 'bilt',
    name: 'Bilt Rewards',
    kind: 'bank',
    typicalCpp: 2.2,
    goodCpp: 2,
    source: 'The Points Guy, Oct 2026 (Frequent Miler floor 1.55)',
    sweetSpots: [
      {
        title: 'Hyatt at 1:1',
        cppLow: 2,
        cppHigh: 4,
      },
      {
        title: 'Alaska Atmos partner awards',
        cppLow: 2,
        cppHigh: 5,
      },
      {
        title: 'Rent Day transfer bonuses',
        cppLow: 2.5,
        cppHigh: 5,
      },
    ],
    transfersTo: [
      '1:1 Aeroplan, Alaska, Avios, Cathay, Emirates, JAL, Qatar, Southwest, Turkish, United, Virgin, Hyatt, Hilton, IHG, Marriott',
    ],
  },
  {
    id: 'wells_fargo',
    name: 'Wells Fargo Rewards',
    kind: 'bank',
    typicalCpp: 1.75,
    goodCpp: 1.7,
    source: 'The Points Guy, Oct 2026 (Frequent Miler floor 1.4)',
    sweetSpots: [
      {
        title: 'Avios or Aer Lingus off-peak business',
        cppLow: 2,
        cppHigh: 4,
      },
      {
        title: 'Flying Blue Promo Rewards',
        cppLow: 1.5,
        cppHigh: 3,
      },
    ],
    transfersTo: [
      '1:1 Aer Lingus, Avios, Cathay, Flying Blue, JetBlue, Virgin',
      '1:2 Choice, Wyndham',
    ],
  },
  {
    id: 'marriott',
    name: 'Marriott Bonvoy',
    kind: 'hotel',
    typicalCpp: 0.75,
    goodCpp: 0.9,
    source: 'The Points Guy, Oct 2026',
    sweetSpots: [
      {
        title: 'Fifth night free on award stays',
        cppLow: 0.9,
        cppHigh: 1.2,
      },
      {
        title: 'Off-peak luxury hotels',
        cppLow: 1,
        cppHigh: 1.5,
      },
    ],
  },
  {
    id: 'hyatt',
    name: 'World of Hyatt',
    kind: 'hotel',
    typicalCpp: 1.65,
    goodCpp: 2,
    source: 'The Points Guy, Oct 2026',
    sweetSpots: [
      {
        title: 'Category 1 to 4 standard nights',
        cppLow: 2,
        cppHigh: 3.5,
      },
      {
        title: 'Top-category luxury and all-inclusive',
        cppLow: 2,
        cppHigh: 4,
      },
      {
        title: 'Suite upgrade awards',
        cppLow: 2.5,
        cppHigh: 5,
      },
    ],
  },
  {
    id: 'hilton',
    name: 'Hilton Honors',
    kind: 'hotel',
    typicalCpp: 0.4,
    goodCpp: 0.5,
    source: 'The Points Guy, Oct 2026',
    sweetSpots: [
      {
        title: 'Fifth night free',
        cppLow: 0.5,
        cppHigh: 0.7,
      },
      {
        title: 'Waldorf Astoria or Conrad at standard prices',
        cppLow: 0.6,
        cppHigh: 1,
      },
    ],
  },
  {
    id: 'ihg',
    name: 'IHG One Rewards',
    kind: 'hotel',
    typicalCpp: 0.6,
    goodCpp: 0.7,
    source: 'The Points Guy, Oct 2026',
    sweetSpots: [
      {
        title: 'Fourth night free with an IHG card',
        cppLow: 0.7,
        cppHigh: 0.9,
      },
    ],
  },
  {
    id: 'wyndham',
    name: 'Wyndham Rewards',
    kind: 'hotel',
    typicalCpp: 0.55,
    goodCpp: 0.9,
    source: 'The Points Guy, Oct 2026',
    sweetSpots: [
      {
        title: 'Lowest-tier hotels in expensive cities',
        cppLow: 1,
        cppHigh: 2,
      },
    ],
  },
  {
    id: 'choice',
    name: 'Choice Privileges',
    kind: 'hotel',
    typicalCpp: 0.85,
    goodCpp: 1,
    source: 'The Points Guy, Oct 2026',
    sweetSpots: [
      {
        title: 'Hotels in Japan and the Nordics',
        cppLow: 1,
        cppHigh: 2,
      },
    ],
  },
  {
    id: 'accor',
    name: 'Accor ALL',
    kind: 'hotel',
    typicalCpp: 2.3,
    goodCpp: 2,
    source: 'The Points Guy, Oct 2026',
    sweetSpots: [
      {
        title: 'Fixed value: 2,000 points take 40 euros off',
        cppLow: 2,
        cppHigh: 2.3,
      },
    ],
  },
  {
    id: 'delta',
    name: 'Delta SkyMiles',
    kind: 'airline',
    typicalCpp: 1.2,
    goodCpp: 1.3,
    source: 'The Points Guy, Oct 2026',
    sweetSpots: [
      {
        title: 'Flash sales: Delta One and Europe economy',
        cppLow: 1.5,
        cppHigh: 3,
      },
      {
        title: 'Virgin or Air France partner business',
        cppLow: 1.5,
        cppHigh: 2.5,
      },
    ],
  },
  {
    id: 'american',
    name: 'American AAdvantage',
    kind: 'airline',
    typicalCpp: 1.45,
    goodCpp: 1.6,
    source: 'The Points Guy, Oct 2026',
    sweetSpots: [
      {
        title: 'Qatar Qsuites and JAL business or first',
        cppLow: 3,
        cppHigh: 7,
      },
      {
        title: 'Off-peak web specials',
        cppLow: 1.5,
        cppHigh: 2.5,
      },
    ],
  },
  {
    id: 'united',
    name: 'United MileagePlus',
    kind: 'airline',
    typicalCpp: 1.2,
    goodCpp: 1.4,
    source: 'The Points Guy, Oct 2026',
    sweetSpots: [
      {
        title: 'Excursionist Perk: a free one-way within a region',
        cppLow: 2,
        cppHigh: 4,
      },
      {
        title: 'Star Alliance business at saver prices',
        cppLow: 2,
        cppHigh: 4,
      },
    ],
  },
  {
    id: 'southwest',
    name: 'Southwest Rapid Rewards',
    kind: 'airline',
    typicalCpp: 1.25,
    goodCpp: 1.4,
    source: 'The Points Guy, Oct 2026',
    sweetSpots: [
      {
        title: 'Companion Pass',
        cppLow: 2,
        cppHigh: 3,
      },
      {
        title: 'Fare sales',
        cppLow: 1.3,
        cppHigh: 1.5,
      },
    ],
  },
  {
    id: 'alaska',
    name: 'Alaska Atmos Rewards',
    kind: 'airline',
    typicalCpp: 1.55,
    goodCpp: 1.8,
    source: 'The Points Guy, Oct 2026',
    sweetSpots: [
      {
        title: 'Cathay, JAL or Qatar business',
        cppLow: 2.5,
        cppHigh: 5,
      },
      {
        title: 'Free stopover on one-way partner awards',
        cppLow: 2,
        cppHigh: 4,
      },
    ],
  },
  {
    id: 'jetblue',
    name: 'JetBlue TrueBlue',
    kind: 'airline',
    typicalCpp: 1.35,
    goodCpp: 1.4,
    source: 'The Points Guy, Oct 2026',
    sweetSpots: [
      {
        title: 'Mint (fixed value)',
        cppLow: 1.3,
        cppHigh: 1.6,
      },
    ],
  },
  {
    id: 'aeroplan',
    name: 'Air Canada Aeroplan',
    kind: 'airline',
    typicalCpp: 1.5,
    goodCpp: 1.8,
    source: 'The Points Guy, Oct 2026',
    sweetSpots: [
      {
        title: 'Business to Europe on Lufthansa, Swiss or United',
        cppLow: 3,
        cppHigh: 6,
        note: 'Long-haul business prices rose in June 2026',
      },
      {
        title: '5,000-point stopover on one-way awards',
        cppLow: 2,
        cppHigh: 4,
      },
      {
        title: 'ANA business to Japan',
        cppLow: 3,
        cppHigh: 5,
      },
    ],
  },
  {
    id: 'flyingblue',
    name: 'Air France KLM Flying Blue',
    kind: 'airline',
    typicalCpp: 1.4,
    goodCpp: 1.5,
    source: 'The Points Guy, Oct 2026',
    sweetSpots: [
      {
        title: 'Monthly Promo Rewards, 20 to 50% off',
        cppLow: 1.5,
        cppHigh: 3,
      },
      {
        title: 'Business to Europe at the lowest price',
        cppLow: 2,
        cppHigh: 4,
      },
    ],
  },
  {
    id: 'avios',
    name: 'British Airways Avios',
    kind: 'airline',
    typicalCpp: 1.4,
    goodCpp: 1.5,
    source: 'The Points Guy, Oct 2026',
    sweetSpots: [
      {
        title: 'Qatar Qsuites booked with Qatar Avios',
        cppLow: 3,
        cppHigh: 6,
      },
      {
        title: 'Aer Lingus off-peak business from the East Coast',
        cppLow: 2,
        cppHigh: 4,
      },
      {
        title: 'Short partner flights',
        cppLow: 1.5,
        cppHigh: 3,
      },
    ],
  },
  {
    id: 'virgin',
    name: 'Virgin Atlantic Flying Club',
    kind: 'airline',
    typicalCpp: 1.5,
    goodCpp: 1.8,
    source: 'The Points Guy, Oct 2026',
    sweetSpots: [
      {
        title: 'ANA business or first to Japan',
        cppLow: 4,
        cppHigh: 8,
      },
      {
        title: 'Delta One',
        cppLow: 1.5,
        cppHigh: 3,
      },
      {
        title: 'Air France or KLM business',
        cppLow: 2,
        cppHigh: 3,
      },
    ],
  },
  {
    id: 'emirates',
    name: 'Emirates Skywards',
    kind: 'airline',
    typicalCpp: 1.2,
    goodCpp: 1.5,
    source: 'The Points Guy, Oct 2026',
    sweetSpots: [
      {
        title: 'A380 first on short flights or sales',
        cppLow: 2,
        cppHigh: 4,
        note: 'High surcharges',
      },
    ],
  },
  {
    id: 'krisflyer',
    name: 'Singapore KrisFlyer',
    kind: 'airline',
    typicalCpp: 1.3,
    goodCpp: 1.6,
    source: 'The Points Guy, Oct 2026',
    sweetSpots: [
      {
        title: 'Saver Suites or First',
        cppLow: 3,
        cppHigh: 6,
      },
      {
        title: 'Spontaneous Escapes discounts',
        cppLow: 1.5,
        cppHigh: 3,
      },
    ],
  },
  {
    id: 'turkish',
    name: 'Turkish Miles&Smiles',
    kind: 'airline',
    typicalCpp: 1.1,
    goodCpp: 1.5,
    source: 'The Points Guy, Oct 2026',
    sweetSpots: [
      {
        title: 'Star Alliance business to Europe',
        cppLow: 2,
        cppHigh: 4,
      },
    ],
  },
  {
    id: 'lifemiles',
    name: 'Avianca LifeMiles',
    kind: 'airline',
    typicalCpp: 1.7,
    goodCpp: 1.5,
    source: 'The Points Guy (about 1.7¢)',
    sweetSpots: [
      {
        title: 'Star Alliance business with no fuel surcharges',
        cppLow: 2,
        cppHigh: 4,
        note: 'Lufthansa, Swiss, Thai and others; cash and miles allowed',
      },
    ],
  },
];

const byId = new Map(PROGRAMS.map((p) => [p.id, p]));

export function programInfo(id: string | null | undefined): ProgramInfo | undefined {
  return id ? byId.get(id) : undefined;
}
